import { BarChart3, FileSearch, Map, Calculator, Shield, Zap } from "lucide-react";

const features = [{
  icon: BarChart3,
  title: "Análisis instantáneo",
  description: "GABI lee anuncios en segundos y te da un resumen claro: precio, riesgos y si encaja contigo."
}, {
  icon: FileSearch,
  title: "Informes detallados",
  description: "PDFs con pros, contras y checklist personalizados. Lo que tu Excel soñaba ser."
}, {
  icon: Map,
  title: "Comparativa de barrios",
  description: "Conoce servicios, transporte y calidad de vida real de cada zona."
}, {
  icon: Calculator,
  title: "Cálculo de hipoteca",
  description: "Simula tu cuota y descubre tu capacidad real sin hablar con cinco bancos."
}, {
  icon: Shield,
  title: "Detección de riesgos",
  description: "Señales de alerta antes de enamorarte de un piso que te puede salir caro (literalmente)."
}, {
  icon: Zap,
  title: "Respuestas al instante",
  description: "Pregunta lo que quieras — GABI te contesta con palabras humanas, no con jerga bancaria."
}];

export const Features = () => {
  return (
    <section id="features" className="py-24 bg-secondary/30 scroll-mt-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold">
            Todo lo que nadie te explicó del proceso de compra,{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
              ahora en una sola pantalla
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Desde calcular tu viabilidad hasta entender si ese piso es buena idea o una trampa disfrazada de ganga.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {features.map(feature => (
            <div key={feature.title} className="glass p-6 rounded-3xl border border-primary/10 hover:border-primary/30 transition-all duration-300 hover:glow-primary group">
              <div className="w-12 h-12 rounded-2xl gradient-primary flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                <feature.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Feature showcases */}
        <div className="space-y-20 max-w-6xl mx-auto">
          {/* Chat showcase */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <span className="text-sm font-semibold text-primary">Chat con IA</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold">
                Habla con GABI como si fuera esa amiga que sí entiende de hipotecas 🧠
              </h3>
              <p className="text-lg text-muted-foreground">
                Sube un anuncio, pega una URL o enséñale un PDF. GABI analiza todo y te responde con claridad brutal: si puedes comprar, cómo mejorar tu perfil y qué opciones tienes si no llegas a la entrada.
              </p>
              <ul className="space-y-3">
                {["Pros y contras de cada piso", "Score de viabilidad real", "Opciones de financiación o alquiler con opción a compra"].map(item => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl" />
              <div className="relative rounded-3xl shadow-2xl border border-primary/20 bg-gradient-to-br from-primary/10 to-accent/10 h-80 flex items-center justify-center">
                <span className="text-4xl">💬</span>
              </div>
            </div>
          </div>

          {/* Opciones reales showcase */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 md:order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-primary/20 rounded-3xl blur-3xl" />
              <div className="relative rounded-3xl shadow-2xl border border-primary/20 bg-gradient-to-br from-accent/10 to-primary/10 h-80 flex items-center justify-center">
                <span className="text-4xl">🏠</span>
              </div>
            </div>
            <div className="space-y-6 order-1 md:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
                <span className="text-sm font-semibold text-accent">Opciones reales</span>
              </div>
              <h3 className="text-3xl md:text-4xl font-bold">¿No tienes ahorros para la entrada? Tranquilo, no estás solo💸</h3>
              <p className="text-lg text-muted-foreground">Si tu perfil es bueno pero te falta la entrada, GABI te muestra opciones reales: desde hipotecas adaptadas hasta alquiler con opción a compra. Tú eliges el piso, nosotros lo compramos, lo alquilas mientras ahorras y cuando estés listo, te lo quedas.</p>
              <ul className="space-y-3">
                {["Alternativas reales según tu situación", "Alquiler con opción a compra gestionado con fondos asociados", "Recomendaciones financieras personalizadas por IA"].map(item => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-accent" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
